import { client } from '@/app/utils/client';
import { dotCache, getCacheKey } from '@/app/utils/cacheService';

export const DOTCMS_URL = new URL(process.env.NEXT_PUBLIC_DOTCMS_HOST);
export const API_URL = `${DOTCMS_URL.origin}/api/v1/graphql`;
const CACHE_KEY = 'top-nav-links';

async function safeJson(res) {
    const text = await res.text();

    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (e) {
        console.error('Failed to parse JSON:', e, text);
        return null;
    }
}

export const getNavLinks = async ({ path = '/', language_id }) => {
    const cacheKey = getCacheKey(CACHE_KEY);
    const cachedData = dotCache.get(cacheKey);

    if (cachedData) {
        return cachedData;
    }

    const data = await client.nav.get(path.replace('index', ''), {
        depth: 3,
        languageId: language_id || 1
    });

    if (data) {
        dotCache.set(cacheKey, data);
    }

    return data;
};

/**
 * Get the GraphQL query for a page
 *
 * @param {*} query
 * @return {*}
 */
export const getGraphQLPageQuery = ({ path, language_id, mode }) => {
    const params = [];

    if (language_id) {
        params.push(`languageId: "${language_id}"`);
    }

    if (mode) {
        params.push(`pageMode: "${mode}"`);
    }

    const paramsString = params.length ? `, ${params.join(', ')}` : '';

    return `
    {
        page(url: "${path}" ${paramsString}) {
            title
            url
            seodescription
            containers {
                path
                identifier
                maxContentlets
                containerStructures {
                    id
                    structureId
                    containerInode
                    containerId
                    code
                    contentTypeVar
                }
                containerContentlets {
                    uuid
                    contentlets {
                        _map
                        ... on calendarEvent {
                            # Related Contentlet
                            location {
                                title
                                url
                                # Related Contentlet
                                activities {
                                    title
                                    urlMap
                                }
                            }
                        }
                    }
                }
            }
            host {
                hostName
            }
            layout {
                header
                footer
                body {
                    rows {
                        columns {
                            widthPercent
                            leftOffset
                            styleClass
                            preview
                            width
                            left
                            containers {
                                identifier
                                uuid
                            }
                        }
                    }
                }
            }
            template {
                iDate
                inode
                identifier
                source
                title
                friendlyName
                modDate
                sortOrder
                showOnMenu
                layout
                menuLabel
                image
                drawed
                drawedBody
            }
            viewAs {
                mode
                visitor {
                  persona {
                    name
                  }
                }
                language {
                  id
                }
            }
        }
    }
    `;
};

/**
 * Fetch content from dotCMS using GraphQL
 *
 * @param {*} query
 * @return {*}
 */
export const getGraphQLData = async (query) => {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${process.env.DOTCMS_AUTH_TOKEN}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query })
    });
    const json = await safeJson(res);
    const data = json?.data;
    return data;
};

export const navQuery = `{
    htmlpageassetCollection(query: "+(conhost:${process.env.NEXT_PUBLIC_DOTCMS_SITE_ID} conhost:SYSTEM_HOST) +live:true") {
        title
        url
        icon
        navigation
        menuTitle
        menuSubtitle
        menuSummary
        showOnMenu
        layout
        menuLabel
        section
        sortOrder
        folder {
            folderPath
            folderTitle
            folderDefaultFileType
            folderSortOrder
            folderName
            folderId
            folderFileMask
        }
        redirecturl
    }
    search(query:"+contentType:FeatureNavCard +live:true") {
        ... on FeatureNavCard {
            title
            label
            heading
            description
            dropdown
            featuredimage {
                fileAsset {
                    idPath
                }
            }
            featuredCta {
                title
                urlName
                url
            }
        }
    }
}`;

export const getGraphQLNavData = async () => {
    // TODO: this can use directly the client
    try {
        const res = await fetch(API_URL, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${process.env.NEXT_PUBLIC_DOTCMS_AUTH_TOKEN}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ query: navQuery })
        });
        const json = await safeJson(res);
        const htmlpageassetCollection = json?.data?.htmlpageassetCollection || [];
        const cmsPromos = json?.data?.search || [];
        const links = await getNavLinks({ path: '/', language_id: 1 });

        const items = updateData(htmlpageassetCollection, links);
        const { dropdowns, navItems } = buildDropdownData(
            htmlpageassetCollection,
            links,
            cmsPromos
        );

        return { items, dropdowns, navItems };
    } catch (error) {
        console.error('Error in getGraphQLNavData:', error);
        return {};
    }
};

function cleanPath(path) {
    return path.replace(/\/+$/, '').replace(/\/index$/, '');
}

function cleanUrl(item) {
    return {
        ...item,
        url: cleanPath(item.url),
        folder: {
            ...item.folder,
            folderPath: cleanPath(item.folder.folderPath)
        }
    };
}

function convertToArray(node) {
    return {
        ...node,
        children: Object.values(node.children)
            .map(convertToArray)
            .sort((a, b) => a.sortOrder - b.sortOrder)
    };
}

function camelCaseText(text) {
    return text
        .replace(/-/g, '')
        .replace(/\s/g, '')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isValidUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://');
}

function extractLinksFromTree(items = [], parent = null) {
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    return items.flatMap((item) => {
        if (item && typeof item === 'object') {
            if (item.type === 'folder') {
                if (Array.isArray(item.children)) {
                    return extractLinksFromTree(item.children, item);
                }
            }

            if (item.type === 'link') {
                const newItem = {
                    ...item,
                    parentHref: parent?.href || null,
                    children: Array.isArray(item.children)
                        ? extractLinksFromTree(item.children, parent)
                        : []
                };

                return [newItem];
            }
        }

        return [];
    });
}

function updateData(data, links) {
    if (!data || !Array.isArray(data)) {
        return [];
    }

    const comparePages = data.filter(
        (item) =>
            Array.isArray(item.showOnMenu) &&
            item.showOnMenu.some((v) => String(v).trim() === 'true') &&
            item.url?.startsWith('/compare')
    );

    const visibleMenuPages = data.filter(
        (item) =>
            Array.isArray(item.showOnMenu) &&
            item.showOnMenu.some((v) => String(v).trim() === 'true') &&
            item.showOnMenu.some((v) => String(v).trim() === 'true')
    );

    const cleanedPages = [...visibleMenuPages, ...comparePages]
        .map((item) => ({
            ...cleanUrl(item),
            type: 'page'
        }))
        .sort((a, b) => a.folder.folderSortOrder - b.folder.folderSortOrder);

    const cleanedLinks = extractLinksFromTree(links?.children)
        .map((link) => ({
            ...link,
            type: 'link',
            title: link.title || '',
            icon: null,
            layout: null,
            menuLabel: link.menuLabel ?? link.title ?? '',
            menuTitle: link.menuTitle ?? link.title ?? '',
            menuSubtitle: null,
            menuSummary: null,
            section: null,
            sortOrder: link.order || 0,
            url: link.href,
            redirecturl: null
        }))
        .filter((link) => isValidUrl(link.url || link.redirecturl));

    const allItems = [...cleanedPages, ...cleanedLinks];
    function buildTree(items) {
        const tree = {};
        const urlMap = {};

        items.forEach((item) => {
            if (item.type === 'link') {
                const urlSegment = item.parentHref?.split('/').filter(Boolean) || [];
                let currentLevel = tree;

                for (let i = 0; i < urlSegment.length; i++) {
                    const segment = urlSegment[i];
                    if (!currentLevel[segment]) {
                        currentLevel[segment] = {
                            id: segment,
                            type: 'group',
                            title: camelCaseText(segment),
                            url: '/' + urlSegment.slice(0, i + 1).join('/'),
                            children: {}
                        };
                    }
                    currentLevel = currentLevel[segment].children;
                }

                currentLevel[item.title] = {
                    url: item.url,
                    title: item.menuTitle || item.title || 'External Link',
                    type: 'link',
                    menuLabel: item.menuLabel || item.title || item.url,
                    sortOrder: item.sortOrder,
                    parentHref: item.parentHref,
                    description: {
                        title: item.menuTitle || item.title,
                        summary: item.menuSummary,
                        subtitle: item.menuSubtitle
                    },
                    children: {}
                };
            } else {
                const realSegment = item.url.split('/').filter(Boolean);

                let treeSegment = (
                    realSegment[0] === 'product'
                        ? ['product', item.section, realSegment.at(-1)]
                        : realSegment[0] === 'compare'
                          ? ['compare', item.section, realSegment.at(-1)]
                          : realSegment
                ).filter(Boolean);

                for (let i = 0; i < treeSegment.length; i++) {
                    const pathSoFar = '/' + treeSegment.slice(0, i + 1).join('/');
                    if (i === treeSegment.length - 1) {
                        urlMap[pathSoFar] = item.redirecturl ?? item.url;
                    } else if (!urlMap[pathSoFar]) {
                        urlMap[pathSoFar] = pathSoFar;
                    }
                }

                let currentLevel = tree;

                treeSegment.forEach((segment, index) => {
                    const segmentPath = '/' + treeSegment.slice(0, index + 1).join('/');
                    const correctUrl = urlMap[segmentPath] || segmentPath;

                    if (!currentLevel[segment]) {
                        currentLevel[segment] = {
                            sortOrder: item.sortOrder,
                            id: segment,
                            type: item.type,
                            icon: index === treeSegment.length - 1 ? item.icon : null,
                            layout: item.layout,
                            parentHref: item.parentHref,
                            menuLabel: item.menuLabel || item.title || segment,
                            title:
                                index === treeSegment.length - 1
                                    ? item.menuTitle || item.title
                                    : camelCaseText(segment),
                            url: cleanPath(correctUrl),
                            description:
                                index === treeSegment.length - 1
                                    ? {
                                          title: item.menuTitle || item.title,
                                          summary: item.menuSummary,
                                          subtitle: item.menuSubtitle
                                      }
                                    : null,
                            children: {}
                        };
                    } else if (index === treeSegment.length - 1) {
                        currentLevel[segment].description = {
                            title: item.menuTitle || item.title,
                            subtitle: item.menuSubtitle,
                            summary: item.menuSummary
                        };
                    }

                    currentLevel = currentLevel[segment].children;
                });
            }
        });

        return Object.values(tree).map(convertToArray);
    }

    return buildTree(allItems);
}

// ---------------------------------------------------------------------------
// Helpers for per-section dropdown builders
// ---------------------------------------------------------------------------

function pageToItem(page) {
    return {
        label: page.menuTitle || page.title,
        href: cleanPath(page.redirecturl || page.url),
        description: page.menuSubtitle || page.menuSummary || null,
        icon: page.icon || null
    };
}

function linkToItem(link) {
    return {
        label: link.title,
        href: link.href,
        description: null,
        icon: null
    };
}

function findPageByUrl(pages, url) {
    return pages.find((p) => cleanPath(p.url) === url);
}

function findLinkByTitle(links, title) {
    return links.find((l) => l.title === title);
}

function pickPages(pages, urls) {
    return urls
        .map((url) => findPageByUrl(pages, url))
        .filter(Boolean)
        .map(pageToItem);
}

function pickLinks(links, titles) {
    return titles
        .map((t) => findLinkByTitle(links, t))
        .filter(Boolean)
        .map(linkToItem);
}

// ---------------------------------------------------------------------------
// Promo transform: CMS FeatureNavCard → internal promo shape
// ---------------------------------------------------------------------------

function transformCmsPromo(cmsPromo) {
    if (!cmsPromo) return null;
    return {
        eyebrow: cmsPromo.label || null,
        title: cmsPromo.heading || null,
        description: cmsPromo.description || null,
        image: cmsPromo.featuredimage?.fileAsset?.idPath || null,
        cta: {
            href: cmsPromo.featuredCta?.url || '#',
            linkName: cmsPromo.featuredCta?.title || 'Read more'
        }
    };
}

// ---------------------------------------------------------------------------
// Product: 2 columns, no headings, 4 + 4 items
// ---------------------------------------------------------------------------

function buildProductDropdown(pages, promo = null) {
    const col1 = pickPages(pages, [
        '/product/headless-cms',
        '/product/page-building',
        '/product/workflows',
        '/product/security-compliance'
    ]);

    const col2 = pickPages(pages, [
        '/product/dotAI',
        '/product/personalization',
        '/product/ab-testing-experiments',
        '/product/features'
    ]);

    return {
        columns: [{ items: col1 }, { items: col2 }],
        promo
    };
}

// ---------------------------------------------------------------------------
// Solutions: 4 columns with headings, pulling from both solutions & product
// ---------------------------------------------------------------------------

function buildSolutionsDropdown(pages, _links, promo = null) {
    const byUseCase = pickPages(pages, [
        '/solutions/web-apps',
        '/solutions/customer-portals',
        '/solutions/intranet-portal',
        '/solutions/multi-site-management',
        '/solutions/knowledge-base',
        '/product/multilingual-localization'
    ]);

    const byIndustry = pickPages(pages, [
        '/demo-request/healthcare',
        '/demo-request/financial-services',
        '/demo-request/government',
        '/demo-request/manufacturing',
        '/demo-request/telecom'
    ]);

    const byRole = pickPages(pages, [
        '/product/developer',
        '/product/marketer',
        '/product/devops',
        '/product/business-user'
    ]);

    const byNeed = [...pickPages(pages, ['/product/dotevergreen', '/integrations', '/services'])];

    return {
        columns: [
            { heading: 'By Use Case', items: byUseCase },
            { heading: 'By Industry', items: byIndustry },
            { heading: 'By Role', items: byRole },
            { heading: 'By Need', items: byNeed }
        ],
        promo
    };
}

// ---------------------------------------------------------------------------
// Developers: 2 columns with headings, merging pages + external links
// ---------------------------------------------------------------------------

function buildDevelopersDropdown(pages, links, promo = null) {
    const toolsAndSdks = [
        ...pickLinks(links, ['Developer Site']),
        ...pickPages(pages, ['/developers/sdks', '/developers/api-playground', '/developers/CLI'])
    ];

    const gettingStarted = [
        ...pickLinks(links, ['Get Started Now']),
        ...pickLinks(links, ['Business Source License (BSL)']),
        ...pickPages(pages, ['/product/dotcms-mcp'])
    ];

    const deployments = pickPages(pages, [
        '/product/cloud-anywhere',
        '/developers/flexible-deployments'
    ]);

    return {
        columns: [
            { heading: 'Tools & SDKs', items: toolsAndSdks },
            { heading: 'Getting Started', items: gettingStarted },
            { heading: 'Deployments', items: deployments }
        ],
        promo
    };
}

// ---------------------------------------------------------------------------
// Resources: 2 columns, no headings, pages in col 1, links in col 2
// ---------------------------------------------------------------------------

function buildResourcesDropdown(pages, links, promo = null) {
    const col1 = pickPages(pages, [
        '/resources/case-studies',
        '/resources/articles',
        '/resources/customer-reviews',
        '/resources/videos',
        '/resources/podcast'
    ]);

    const communityLink = findLinkByTitle(links, 'Join the dotCMS Community on Discourse');
    const col2 = [
        communityLink
            ? { ...linkToItem(communityLink), label: 'Join our Community' }
            : {
                  label: 'Join our Community',
                  href: 'https://community.dotcms.com/',
                  description: 'Connect with other dotCMS users',
                  icon: null
              },
        ...pickLinks(links, ['Subscribe to dotDEV', 'Subscribe to dotNEWS']),
        ...pickPages(pages, ['/awards']),
        {
            label: 'Newsroom',
            href: '/news',
            description: 'Latest news and press releases from dotCMS',
            icon: null
        }
    ];

    return {
        columns: [{ items: col1 }, { items: col2 }],
        promo
    };
}

// ---------------------------------------------------------------------------
// Coordinator: assembles all dropdowns + navItems
// ---------------------------------------------------------------------------

function buildDropdownData(pages, navLinks, cmsPromos = []) {
    const allPages = pages.filter(
        (item) =>
            Array.isArray(item.showOnMenu) &&
            item.showOnMenu.some((v) => String(v).trim() === 'true')
    );

    const allLinks = extractLinksFromTree(navLinks?.children).filter((link) =>
        isValidUrl(link.href)
    );

    // Map each CMS promo to its target dropdown via the `dropdown` field
    const promoByDropdown = (Array.isArray(cmsPromos) ? cmsPromos : []).reduce((acc, item) => {
        if (item?.dropdown) acc[item.dropdown] = transformCmsPromo(item);
        return acc;
    }, {});

    const dropdowns = {
        product: buildProductDropdown(allPages, promoByDropdown.product ?? null),
        solutions: buildSolutionsDropdown(allPages, allLinks, promoByDropdown.solutions ?? null),
        developers: buildDevelopersDropdown(allPages, allLinks, promoByDropdown.developers ?? null),
        resources: buildResourcesDropdown(allPages, allLinks, promoByDropdown.resources ?? null)
    };

    const pricingPage = allPages.find((p) => p.folder.folderName === 'pricing');
    const partnersPage = allPages.find((p) => p.folder.folderName === 'partners');

    const navItems = [
        { key: 'product', label: 'Product', hasDropdown: true },
        { key: 'solutions', label: 'Solutions', hasDropdown: true },
        { key: 'developers', label: 'Developers', hasDropdown: true },
        { key: 'resources', label: 'Resources', hasDropdown: true },
        {
            key: 'ecosystem',
            label: 'Ecosystem',
            hasDropdown: false,
            href: partnersPage
                ? cleanPath(partnersPage.redirecturl || partnersPage.url)
                : '/partners'
        },
        {
            key: 'pricing',
            label: 'Pricing',
            hasDropdown: false,
            href: pricingPage ? cleanPath(pricingPage.redirecturl || pricingPage.url) : '/pricing'
        }
    ];

    return { dropdowns, navItems };
}
